import type { ColumnDef, Header } from "@tanstack/react-table";
import { useRouter } from "next/router";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useDebounceValue } from "usehooks-ts";

import { Table } from "@/components/ui/table/Table";
import TableCell from "@/components/ui/table/TableCell";
import TableContentCell from "@/components/ui/table/TableContentCell";
import TableHeaderCell from "@/components/ui/table/TableHeaderCell";
import { Typography } from "@/components/ui/text/Typography/Typography";
import { UploadsPageTableState, experienceFilter } from "@/components/uploads/UploadsPage";
import { RouteConfig } from "@/config/route.config";
import type { BizProfileModels } from "@/data/bizProfile/bizProfile.models";
import { BizProfileQueries } from "@/data/bizProfile/bizProfile.queries";
import { useLocalStorage } from "@/hooks/useLocalStorage";
import { NumberUtils } from "@/util/number.utils";

interface SuggestedProfilesProps {
  searchTerm: string;
  metroArea: string;
  quantileMin: number;
  quantileMax: number;
}

export const SuggestedProfiles = ({ searchTerm, metroArea, quantileMin, quantileMax }: SuggestedProfilesProps) => {
  const { t } = useTranslation();
  const router = useRouter();
  const [debouncedSearchTerm] = useDebounceValue(searchTerm, 500);
  const { setValue: updateTableStateLocalStorage } = useLocalStorage(UploadsPageTableState);
  const { data: suggestedProfiles, isLoading: isSuggestedProfilesLoading } = BizProfileQueries.useList({
    limit: 5,
    order: "rank",
    page: 1,
    filterExperienceYearsMax: quantileMax,
    filterExperienceYearsMin: quantileMin,
    filterJobTitle: debouncedSearchTerm,
    filterMsaLocation: metroArea,
  });

  const columns = useMemo<ColumnDef<BizProfileModels.BizProfileListDto>[]>(
    () => [
      {
        accessorKey: "name",
        header: ({ header }) => ProfileHeaderCell(header, t("employees.table.name")),
        size: 200,
        cell: (info) => ProfileCell(info, "name"),
      },
      {
        accessorKey: "jobTitle",
        header: ({ header }) => ProfileHeaderCell(header, t("employees.table.position")),
        size: 200,
        cell: (info) => ProfileCell(info, "jobTitle"),
      },
      {
        accessorKey: "ldNumber",
        header: ({ header }) => ProfileHeaderCell(header, t("employees.table.lossDogValue")),
        size: 120,
        cell: (info) => ProfileCell(info, "ldNumber"),
      },
      {
        accessorKey: "rank",
        header: ({ header }) => ProfileHeaderCell(header, t("employees.table.rank")),
        size: 120,
        cell: (info) => ProfileCell(info, "rank"),
      },
    ],
    [t],
  );

  const seeAllData = useMemo(() => {
    if (!suggestedProfiles?.totalItems || suggestedProfiles.totalItems <= 5) {
      return undefined;
    }

    return {
      totalItemsCount: suggestedProfiles.totalItems,
      onSeeAll: () => {
        // Update localStorage with the current filters
        const experienceFilterId = experienceFilter.find(
          (exp: { id: string; min?: number; max?: number }) => exp.min === quantileMin && exp.max === quantileMax,
        )?.id;

        updateTableStateLocalStorage((curr) => ({
          ...curr,
          selectedTags: {
            ...curr?.selectedTags,
            experience: experienceFilterId ? [experienceFilterId] : [],
            location: metroArea ? [metroArea] : [],
          },
          searchValue: debouncedSearchTerm || "",
          pagination: {
            pageIndex: 0,
            pageSize: 10,
          },
        }));
        router.push(RouteConfig.uploads);
      },
      buttonText: t("dashboard.suggestedProfiles.seeAll", { count: suggestedProfiles.totalItems }),
    };
  }, [
    suggestedProfiles?.totalItems,
    router,
    t,
    quantileMin,
    quantileMax,
    metroArea,
    debouncedSearchTerm,
    updateTableStateLocalStorage,
  ]);

  if (suggestedProfiles?.items && suggestedProfiles.items.length === 0 && !isSuggestedProfilesLoading) return null;

  return (
    <div className="flex flex-col gap-2">
      <Typography
        size="label-4"
        variant="prominent-1"
        className="text-text-inverted-secondary uppercase"
      >
        {t("dashboard.suggestedProfiles.title")}
      </Typography>
      <Table
        data={suggestedProfiles?.items || []}
        columns={columns}
        sorting={[]}
        onSortingChange={() => {}}
        seeAllData={seeAllData}
        tableLayout="auto"
        isLoading={isSuggestedProfilesLoading}
      />
    </div>
  );
};

const ProfileHeaderCell = (header: Header<any, unknown>, label: string) => {
  if (label === "Rank") {
    return (
      <TableHeaderCell
        align="center"
        header={header}
        label={label}
      />
    );
  }
  return (
    <TableHeaderCell
      align="start"
      header={header}
      label={label}
    />
  );
};

const ProfileCell = (info: any, key: string) => {
  if (key === "rank") {
    return (
      <TableContentCell align="center">
        <Typography size="body-4">#{info.row.original.rank}</Typography>
      </TableContentCell>
    );
  }
  if (key === "ldNumber") {
    return (
      <TableContentCell align="start">
        <Typography size="body-4">{NumberUtils.formatAsUSCurrency(info.row.original.jobSalary || 0)}</Typography>
      </TableContentCell>
    );
  }

  if (key === "jobTitle") {
    return (
      <TableContentCell
        className="!max-w-36"
        align="start"
      >
        <Typography
          size="body-4"
          variant="default"
          className="min-w-12 whitespace-pre-line break-words text-text-inverted-secondary"
        >
          {info.getValue()}
        </Typography>
      </TableContentCell>
    );
  }

  return (
    <TableCell align="start">
      <Typography size="body-4">{info.getValue()}</Typography>
    </TableCell>
  );
};
